import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Plus, Trash2, Users, BarChart3, AlertTriangle, Calendar, Download, ChevronLeft, ChevronRight, FolderOpen, Edit2, BadgeDollarSign, UserPlus, UserCircle, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Product, Resource, ResourceAllocation, ResourceRole, FULL_TIME_HOURS_PER_MONTH, DEFAULT_RESOURCE_ROLE_NAMES, ProductPricingModel, PRODUCT_PRICING_MODEL_OPTIONS, getProductPricingModelLabel } from '../types';
import { getMonthCapacityHours, sumMonthCapacities, WORKING_HOURS_PER_DAY } from '../utils/workCalendar';
import {
  formatCalendarDateLabel,
  formatDateForInput,
  parseOptionalDateInput,
} from '../utils/calendarDate';
import { sortResourcesByName } from '../utils/resourceList';
import { 
  addResource, 
  updateResource, 
  deleteResource, 
  subscribeToResources,
  upsertAllocation,
  deleteAllocation,
  updateAllocationResourceId,
  subscribeToAllocations,
  subscribeToResourceRoles,
  addResourceRole,
  updateResourceRole,
  deleteResourceRole,
  renameResourceRoleAndResources,
  seedDefaultResourceRoles,
  addProduct,
  updateProduct,
  deleteProduct,
  ensureResourceManagerProjectsColumn,
} from '../firebase/firestore';

interface ResourceManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  products: Product[];
  /** When true, render as full page (no modal overlay). Used for standalone Resource Manager board. */
  isStandalone?: boolean;
  /** Column id for creating projects (standalone only). */
  projectsColumnId?: string;
  /** Board name for standalone header. */
  boardName?: string;
}

const ResourceManagerModal: React.FC<ResourceManagerModalProps> = ({
  isOpen,
  onClose,
  boardId,
  products,
  isStandalone = false,
  projectsColumnId,
  boardName,
}) => {
  const { user } = useAuth();
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectStartDate, setNewProjectStartDate] = useState('');
  const [newProjectEndDate, setNewProjectEndDate] = useState('');
  const [newProjectPricingModel, setNewProjectPricingModel] = useState<ProductPricingModel>('time_and_materials');
  /** Optional role slots to create as placeholder resources when adding a project (e.g. Developer, Developer, PM). */
  const [newProjectPlaceholderSlots, setNewProjectPlaceholderSlots] = useState<string[]>([]);
  const [creatingProject, setCreatingProject] = useState(false);
  const [activeTab, setActiveTab] = useState<'assignments' | 'resources' | 'team' | 'timeline' | 'projects' | 'roles'>('assignments');
  const [resources, setResources] = useState<Resource[]>([]);
  const [resourceRoles, setResourceRoles] = useState<ResourceRole[]>([]);
  const [allocations, setAllocations] = useState<ResourceAllocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Project edit/delete (standalone Projects tab)
  const [editingProject, setEditingProject] = useState<Product | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<Product | null>(null);
  const [deletingProject, setDeletingProject] = useState(false);

  // Resource form state
  const [newResource, setNewResource] = useState({
    name: '',
    role: 'Developer',
  });
  const [newRoleForm, setNewRoleForm] = useState({ name: '', billRatePerHour: '', costRatePerHour: '' });
  const [editingRoleDraft, setEditingRoleDraft] = useState<ResourceRole | null>(null);
  const [seedingRoles, setSeedingRoles] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);

  // Allocation state
  const [allocationValues, setAllocationValues] = useState<Record<string, Record<string, number>>>({});
  // Assignment modal (drag-drop or add with monthly hours/dates)
  const [assignmentModal, setAssignmentModal] = useState<{ resourceId: string; resourceName: string; productId: string; productName: string } | null>(null);
  const [assignmentForm, setAssignmentForm] = useState({
    hoursPerMonth: 80,
    startDate: '',
    endDate: '',
  });

  /** Assignments view: by resource (each resource's projects) or by project (each project's resources). */
  const [assignmentsView, setAssignmentsView] = useState<'by_resource' | 'by_project'>('by_resource');
  /** Filter roster lists by job role (By resource + Resources tab). */
  const [resourceViewRoleFilter, setResourceViewRoleFilter] = useState<string>('all');
  /** Resource whose project multi-select dropdown is open. */
  const [projectSelectResourceId, setProjectSelectResourceId] = useState<string | null>(null);
  /** Project whose "add resource" multi-select is open (by-project view). */
  const [resourceSelectProductId, setResourceSelectProductId] = useState<string | null>(null);
  /** Project whose "add placeholder role" dropdown is open. */
  const [placeholderRolePickerProductId, setPlaceholderRolePickerProductId] = useState<string | null>(null);
  /** Pending allocation removal that needs user confirmation (has entered data). */
  const [removalConfirm, setRemovalConfirm] = useState<{
    resourceId: string;
    productId: string;
    allocationId: string;
    resourceName: string;
    productName: string;
  } | null>(null);
  const [removingAllocation, setRemovingAllocation] = useState(false);

  /** Replace placeholder with a real resource. */
  const [replaceModal, setReplaceModal] = useState<{
    allocationId: string;
    placeholderId: string;
    placeholderLabel: string;
    productId: string;
    productName: string;
  } | null>(null);
  const [replacementResourceId, setReplacementResourceId] = useState('');
  /** Draft value for the 12-month grid cell being edited (saved on blur). */
  const [monthlyEdit, setMonthlyEdit] = useState<{ resourceId: string; productId: string; monthKey: string; value: string } | null>(null);

  /** Assignments By resource: show only these resource rows; null = all (default). */
  const [assignmentsResourceIdsFilter, setAssignmentsResourceIdsFilter] = useState<string[] | null>(null);
  /** Assignments By project: show only these project rows; null = all (default). */
  const [assignmentsProductIdsFilter, setAssignmentsProductIdsFilter] = useState<string[] | null>(null);
  const [assignmentsResourceFilterOpen, setAssignmentsResourceFilterOpen] = useState(false);
  const [assignmentsProjectFilterOpen, setAssignmentsProjectFilterOpen] = useState(false);

  // Timeline view
  const [timelineMonth, setTimelineMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  useEffect(() => {
    if (!isOpen || !boardId || !user) return;

    setLoading(true);
    setError(null);

    // Subscribe to resources
    const unsubscribeResources = subscribeToResources(boardId, (resourcesData) => {
      setResources(resourcesData);
    });

    // Subscribe to allocations (store monthly hours in allocationValues)
    const unsubscribeAllocations = subscribeToAllocations(boardId, (allocationsData) => {
      setAllocations(allocationsData);
      const values: Record<string, Record<string, number>> = {};
      allocationsData.forEach(allocation => {
        if (!values[allocation.resourceId]) {
          values[allocation.resourceId] = {};
        }
        let hours: number;
        if (allocation.monthlyHours && Object.keys(allocation.monthlyHours).length > 0) {
          hours = Object.values(allocation.monthlyHours).reduce((s, v) => s + v, 0) / 12;
        } else {
          hours = allocation.hoursPerMonth ?? (allocation.percent * FULL_TIME_HOURS_PER_MONTH / 100);
        }
        values[allocation.resourceId][allocation.productId] = hours;
      });
      setAllocationValues(values);
    });

    setLoading(false);

    const unsubscribeRoles = subscribeToResourceRoles(
      boardId,
      setResourceRoles,
      (err) => setError(`Could not load roles: ${err.message}`)
    );

    return () => {
      unsubscribeResources();
      unsubscribeAllocations();
      unsubscribeRoles();
    };
  }, [isOpen, boardId, user]);

  /** Ensure standard role rows exist in Firestore so Roles tab and dropdowns stay in sync (idempotent). */
  useEffect(() => {
    if (!isOpen || !boardId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        await seedDefaultResourceRoles(boardId, user.id);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Could not sync standard role definitions');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, boardId, user]);

  const handleAddResource = async () => {
    if (!user || !newResource.name.trim()) return;

    try {
      setLoading(true);
      await addResource({
        name: newResource.name.trim(),
        role: newResource.role,
        boardId,
        createdBy: user.id
      }, user.id);
      
      setNewResource({ name: '', role: 'Developer' });
    } catch (err) {
      setError('Failed to add resource');
      console.error('Error adding resource:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateResource = async (resourceId: string, updates: Partial<Resource>) => {
    if (!user) return;

    try {
      setLoading(true);
      await updateResource(resourceId, updates);
      setEditingResource(null);
    } catch (err) {
      setError('Failed to update resource');
      console.error('Error updating resource:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!user) return;
    const resource = resources.find((r) => r.id === resourceId);
    const label = resource?.name?.trim() || 'this resource';
    const rolePart = resource?.role ? ` — ${resource.role}` : '';
    const placeholderNote = resource?.isPlaceholder
      ? ' This placeholder will be removed from all projects.'
      : ' All project assignments for this person will be removed.';
    if (
      !window.confirm(
        `Delete "${label}"${rolePart}?${placeholderNote} This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await deleteResource(resourceId);
    } catch (err) {
      setError('Failed to delete resource');
      console.error('Error deleting resource:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaultRoles = async () => {
    if (!user) return;
    try {
      setSeedingRoles(true);
      setError(null);
      await seedDefaultResourceRoles(boardId, user.id);
    } catch (err) {
      setError('Failed to add default roles');
      console.error(err);
    } finally {
      setSeedingRoles(false);
    }
  };

  const handleAddRole = async () => {
    if (!user) return;
    const name = newRoleForm.name.trim();
    if (!name) {
      setError('Enter a role name');
      return;
    }
    const dup = resourceRoles.some(r => r.name.toLowerCase() === name.toLowerCase());
    if (dup) {
      setError('A role with that name already exists');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await addResourceRole(
        {
          boardId,
          name,
          billRatePerHour: parseFloat(newRoleForm.billRatePerHour) || 0,
          costRatePerHour: parseFloat(newRoleForm.costRatePerHour) || 0,
        },
        user.id
      );
      setNewRoleForm({ name: '', billRatePerHour: '', costRatePerHour: '' });
    } catch (err) {
      setError('Failed to add role');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRoleRow = async (original: ResourceRole) => {
    if (!user || !editingRoleDraft || editingRoleDraft.id !== original.id) return;
    const name = editingRoleDraft.name.trim();
    if (!name) {
      setError('Role name is required');
      return;
    }
    const dup = resourceRoles.some(
      r => r.id !== original.id && r.name.toLowerCase() === name.toLowerCase()
    );
    if (dup) {
      setError('Another role already uses that name');
      return;
    }
    const bill = Number(editingRoleDraft.billRatePerHour) || 0;
    const cost = Number(editingRoleDraft.costRatePerHour) || 0;
    try {
      setLoading(true);
      setError(null);
      if (name !== original.name) {
        await renameResourceRoleAndResources(original.id, boardId, original.name, name);
      }
      await updateResourceRole(original.id, { billRatePerHour: bill, costRatePerHour: cost });
      setEditingRoleDraft(null);
    } catch (err) {
      setError('Failed to update role');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (role: ResourceRole) => {
    const inUse = resources.filter(r => r.role === role.name).length;
    if (inUse > 0) {
      setError(`Cannot delete "${role.name}": ${inUse} resource(s) use this role. Reassign them first.`);
      return;
    }
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteResourceRole(role.id);
      if (editingRoleDraft?.id === role.id) setEditingRoleDraft(null);
    } catch (err) {
      setError('Failed to delete role');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /** 12 month keys starting from current month: ["2025-02", "2025-03", ...]. */
  const getMonthKeys = useCallback(() => {
    const keys: string[] = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      d.setMonth(d.getMonth() + 1);
    }
    return keys;
  }, []);

  /** All selectable role names: Firestore roles + defaults + any role string already on a resource. */
  const roleNameOptions = useMemo(() => {
    const names = new Set<string>();
    resourceRoles.forEach(r => {
      if (r.name?.trim()) names.add(r.name.trim());
    });
    DEFAULT_RESOURCE_ROLE_NAMES.forEach(n => names.add(n));
    resources.forEach(r => {
      if (r.role?.trim()) names.add(r.role.trim());
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [resourceRoles, resources]);

  const resourcesMatchingRoleFilter = useMemo(() => {
    const filtered =
      resourceViewRoleFilter === 'all'
        ? resources
        : resources.filter((r) => r.role === resourceViewRoleFilter);
    return sortResourcesByName(filtered);
  }, [resources, resourceViewRoleFilter]);

  const resourcesSortedByName = useMemo(() => sortResourcesByName(resources), [resources]);

  const nonPlaceholderResourcesSortedByName = useMemo(
    () => resourcesSortedByName.filter((r) => !r.isPlaceholder),
    [resourcesSortedByName]
  );

  const productsSortedByName = useMemo(
    () =>
      [...products].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ),
    [products]
  );

  const assignmentsResourceFilterPoolIds = useMemo(
    () => resourcesMatchingRoleFilter.map((r) => r.id),
    [resourcesMatchingRoleFilter]
  );

  const assignmentsProductFilterPoolIds = useMemo(
    () => productsSortedByName.map((p) => p.id),
    [productsSortedByName]
  );

  const resourcesVisibleInAssignmentsByResource = useMemo(() => {
    if (assignmentsResourceIdsFilter === null) return resourcesMatchingRoleFilter;
    const allow = new Set(assignmentsResourceIdsFilter);
    return resourcesMatchingRoleFilter.filter((r) => allow.has(r.id));
  }, [resourcesMatchingRoleFilter, assignmentsResourceIdsFilter]);

  const productsVisibleInAssignmentsByProject = useMemo(() => {
    if (assignmentsProductIdsFilter === null) return productsSortedByName;
    const allow = new Set(assignmentsProductIdsFilter);
    return productsSortedByName.filter((p) => allow.has(p.id));
  }, [productsSortedByName, assignmentsProductIdsFilter]);

  const toggleAssignmentsResourceFilter = useCallback(
    (resourceId: string) => {
      setAssignmentsResourceIdsFilter((prev) => {
        const pool = assignmentsResourceFilterPoolIds;
        if (pool.length === 0) return prev;
        if (prev === null) {
          const next = pool.filter((id) => id !== resourceId);
          return next.length === pool.length ? null : next;
        }
        const set = new Set(prev);
        if (set.has(resourceId)) set.delete(resourceId);
        else set.add(resourceId);
        const next = pool.filter((id) => set.has(id));
        if (next.length === 0) return [];
        if (next.length === pool.length) return null;
        return next;
      });
    },
    [assignmentsResourceFilterPoolIds]
  );

  const toggleAssignmentsProductFilter = useCallback(
    (productId: string) => {
      setAssignmentsProductIdsFilter((prev) => {
        const pool = assignmentsProductFilterPoolIds;
        if (pool.length === 0) return prev;
        if (prev === null) {
          const next = pool.filter((id) => id !== productId);
          return next.length === pool.length ? null : next;
        }
        const set = new Set(prev);
        if (set.has(productId)) set.delete(productId);
        else set.add(productId);
        const next = pool.filter((id) => set.has(id));
        if (next.length === 0) return [];
        if (next.length === pool.length) return null;
        return next;
      });
    },
    [assignmentsProductFilterPoolIds]
  );

  /** First month (from current) where resource total hours < 50% of full time (80h). Returns short label e.g. "Oct" or null. Only when resource has monthly allocation data. */
  const getFreeNextMonth = (resourceId: string): string | null => {
    const resourceAllocs = allocations.filter(a => a.resourceId === resourceId);
    const hasMonthlyData = resourceAllocs.some(a => a.monthlyHours && Object.keys(a.monthlyHours).length > 0);
    if (!hasMonthlyData) return null;
    const monthKeys = getMonthKeys();
    for (const monthKey of monthKeys) {
      const cap = getMonthCapacityHours(monthKey);
      const threshold = cap * 0.5;
      const total = resourceAllocs.reduce((sum, a) => sum + (a.monthlyHours?.[monthKey] ?? 0), 0);
      if (total < threshold) {
        const [y, m] = monthKey.split('-');
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1).toLocaleDateString('en-US', { month: 'short' });
      }
    }
    return null;
  };

  /** Product IDs this resource is assigned to (has an allocation). */
  const getAssignedProductIds = (resourceId: string): string[] => {
    return allocations.filter(a => a.resourceId === resourceId).map(a => a.productId);
  };

  const handleProjectSelect = async (resourceId: string, productId: string, selected: boolean) => {
    if (!user) return;
    if (selected) {
      await upsertAllocation({
        resourceId,
        productId,
        boardId,
        percent: 0,
        monthlyHours: {},
        createdBy: user.id,
      }, user.id);
    } else {
      const alloc = allocations.find(a => a.resourceId === resourceId && a.productId === productId);
      if (!alloc) return;

      // Check if the allocation has any entered data (non-zero hours or percent).
      const hasData =
        (alloc.percent != null && alloc.percent > 0) ||
        Object.values(alloc.monthlyHours ?? {}).some(h => h > 0);

      if (hasData) {
        // Warn the user before wiping entered data.
        const resource = resources.find(r => r.id === resourceId);
        const product = products.find(p => p.id === productId);
        setRemovalConfirm({
          resourceId,
          productId,
          allocationId: alloc.id,
          resourceName: resource?.name ?? 'this resource',
          productName: product?.name ?? 'this project',
        });
      } else {
        await deleteAllocation(alloc.id);
      }
    }
  };

  const handleConfirmRemoval = async () => {
    if (!removalConfirm) return;
    setRemovingAllocation(true);
    try {
      await deleteAllocation(removalConfirm.allocationId);
      setRemovalConfirm(null);
    } finally {
      setRemovingAllocation(false);
    }
  };

  const getMonthlyDisplayValue = (resourceId: string, productId: string, monthKey: string): string => {
    if (monthlyEdit?.resourceId === resourceId && monthlyEdit?.productId === productId && monthlyEdit?.monthKey === monthKey) {
      return monthlyEdit.value;
    }
    const alloc = allocations.find(a => a.resourceId === resourceId && a.productId === productId);
    const h = alloc?.monthlyHours?.[monthKey] ?? 0;
    return h === 0 ? '' : String(h);
  };

  const handleSaveMonthlyHours = async (resourceId: string, productId: string, monthKey: string, hours: number) => {
    if (!user) return;
    setMonthlyEdit(null);
    const alloc = allocations.find(a => a.resourceId === resourceId && a.productId === productId);
    if (!alloc) return;
    const nextMonthly = { ...(alloc.monthlyHours || {}), [monthKey]: hours };
    if (hours === 0) delete nextMonthly[monthKey];
    const sumHours = Object.values(nextMonthly).reduce((s, v) => s + v, 0);
    await upsertAllocation({
      resourceId: alloc.resourceId,
      productId: alloc.productId,
      boardId: alloc.boardId,
      percent: (() => {
        const capSum = sumMonthCapacities(getMonthKeys());
        return sumHours > 0 && capSum > 0 ? Math.round((sumHours / capSum) * 100) : 0;
      })(),
      monthlyHours: Object.keys(nextMonthly).length > 0 ? nextMonthly : undefined,
      startDate: alloc.startDate,
      endDate: alloc.endDate,
      createdBy: alloc.createdBy,
    }, user.id);
  };

  const getResourceTotalAllocation = (resourceId: string): number => {
    const resourceAllocations = allocationValues[resourceId] || {};
    return Object.values(resourceAllocations).reduce((sum, h) => sum + h, 0);
  };

  /** Availability vs capacity (defaults to legacy 160h when capacity omitted). */
  const getAvailabilityColor = (hours: number, capacity?: number): string => {
    const cap = capacity != null && capacity > 0 ? capacity : FULL_TIME_HOURS_PER_MONTH;
    if (hours > cap) return 'bg-red-500';
    if (hours >= cap * 0.8) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  /** Background style for a month column by hours vs that month’s working capacity. */
  const getMonthColumnStyle = (hours: number, monthKey: string): React.CSSProperties => {
    const cap = getMonthCapacityHours(monthKey);
    if (cap <= 0) return { backgroundColor: 'rgb(243, 244, 246)' };
    if (hours > cap) return { backgroundColor: 'rgb(254, 202, 202)' }; // red-200
    if (hours <= 0) return { backgroundColor: 'rgb(209, 250, 229)' }; // emerald-100
    const t = Math.min(hours, cap) / cap;
    const r = Math.round(209 + (254 - 209) * t);
    const g = Math.round(250 + (215 - 250) * t);
    const b = Math.round(229 + (170 - 229) * t);
    return { backgroundColor: `rgb(${r}, ${g}, ${b})` };
  };

  /** Total hours for a resource in a given month (includes current draft from monthlyEdit). */
  const getResourceMonthTotal = (resourceId: string, monthKey: string, assignedProductIds: string[]): number => {
    return assignedProductIds.reduce((sum, productId) => {
      const alloc = allocations.find(a => a.resourceId === resourceId && a.productId === productId);
      const editVal = monthlyEdit?.resourceId === resourceId && monthlyEdit?.productId === productId && monthlyEdit?.monthKey === monthKey
        ? parseInt(monthlyEdit.value, 10) || 0
        : null;
      const h = editVal !== null ? editVal : (alloc?.monthlyHours?.[monthKey] ?? 0);
      return sum + h;
    }, 0);
  };

  /** Average of each month’s utilization % (hours ÷ that month’s capacity) over the next 3 months. */
  const getResourceAvgAllocationPctNext3Months = (resourceId: string, assignedProductIds: string[]): number | null => {
    if (assignedProductIds.length === 0) return null;
    const monthKeys = getMonthKeys().slice(0, 3);
    let sumPct = 0;
    for (const monthKey of monthKeys) {
      const cap = getMonthCapacityHours(monthKey);
      const h = getResourceMonthTotal(resourceId, monthKey, assignedProductIds);
      sumPct += cap > 0 ? (h / cap) * 100 : 0;
    }
    return Math.round(sumPct / monthKeys.length);
  };

  /** Average monthly hours over the next 3 months (current + 2). Returns null if no assigned projects. */
  const getResourceAvgHoursNext3Months = (resourceId: string, assignedProductIds: string[]): number | null => {
    if (assignedProductIds.length === 0) return null;
    const monthKeys = getMonthKeys().slice(0, 3);
    const totalHours = monthKeys.reduce((sum, monthKey) => sum + getResourceMonthTotal(resourceId, monthKey, assignedProductIds), 0);
    return totalHours / monthKeys.length;
  };

  /** Resources assigned to a project (have an allocation to this product). Placeholders sort first. */
  const getResourcesForProject = (productId: string): Resource[] => {
    const resourceIds = Array.from(new Set(allocations.filter(a => a.productId === productId).map(a => a.resourceId)));
    const list = resourceIds.map(id => resources.find(r => r.id === id)).filter((r): r is Resource => r != null);
    return list.sort((a, b) => {
      const pa = a.isPlaceholder ? 0 : 1;
      const pb = b.isPlaceholder ? 0 : 1;
      if (pa !== pb) return pa - pb;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  };

  const handleAddPlaceholderToProject = async (productId: string, roleName: string) => {
    if (!user || !roleName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const assigned = getResourcesForProject(productId);
      const idx = assigned.filter((r) => r.isPlaceholder && r.role === roleName.trim()).length;
      const name = `Placeholder ${roleName.trim()} ${idx + 1}`;
      const resourceId = await addResource(
        {
          name,
          role: roleName.trim(),
          boardId,
          isPlaceholder: true,
          createdBy: user.id,
        },
        user.id
      );
      await upsertAllocation(
        {
          resourceId,
          productId,
          boardId,
          percent: 0,
          monthlyHours: {},
          createdBy: user.id,
        },
        user.id
      );
      setPlaceholderRolePickerProductId(null);
    } catch (err) {
      setError('Failed to add placeholder');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReplacePlaceholder = async () => {
    if (!user || !replaceModal || !replacementResourceId.trim()) return;
    const { allocationId, placeholderId, productId } = replaceModal;
    const newId = replacementResourceId;
    if (newId === placeholderId) return;
    const conflict = allocations.some(
      (a) => a.resourceId === newId && a.productId === productId && a.id !== allocationId
    );
    if (conflict) {
      setError('That person is already assigned to this project. Remove the duplicate assignment first.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await updateAllocationResourceId(allocationId, newId);
      await deleteResource(placeholderId);
      setReplaceModal(null);
      setReplacementResourceId('');
    } catch (err) {
      setError('Failed to replace placeholder');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /** Total hours for a project in a given month across all assigned resources (includes draft). */
  const getProjectMonthTotal = (productId: string, monthKey: string): number => {
    return allocations
      .filter(a => a.productId === productId)
      .reduce((sum, a) => {
        const editVal = monthlyEdit?.resourceId === a.resourceId && monthlyEdit?.productId === a.productId && monthlyEdit?.monthKey === monthKey
          ? parseInt(monthlyEdit.value, 10) || 0
          : null;
        const h = editVal !== null ? editVal : (a.monthlyHours?.[monthKey] ?? 0);
        return sum + h;
      }, 0);
  };

  const overAllocatedResourceIds = resources.filter(r => {
    const ids = getAssignedProductIds(r.id);
    if (ids.length === 0) return getResourceTotalAllocation(r.id) > FULL_TIME_HOURS_PER_MONTH;
    return getMonthKeys().some(mk => getResourceMonthTotal(r.id, mk, ids) > getMonthCapacityHours(mk));
  }).map(r => r.id);

  const handleSaveAssignmentModal = useCallback(async () => {
    if (!user || !assignmentModal) return;
    const { resourceId, productId } = assignmentModal;
    const startDate = parseOptionalDateInput(assignmentForm.startDate);
    const endDate = parseOptionalDateInput(assignmentForm.endDate);
    const hours = assignmentForm.hoursPerMonth;
    try {
      setLoading(true);
      await upsertAllocation({
        resourceId,
        productId,
        boardId,
        percent: Math.round((hours / FULL_TIME_HOURS_PER_MONTH) * 100),
        hoursPerMonth: hours,
        startDate,
        endDate,
        createdBy: user.id,
      }, user.id);
      setAllocationValues(prev => ({
        ...prev,
        [resourceId]: {
          ...prev[resourceId],
          [productId]: hours,
        },
      }));
      setAssignmentModal(null);
      setAssignmentForm({ hoursPerMonth: 80, startDate: '', endDate: '' });
    } catch (err) {
      setError('Failed to save assignment');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user, boardId, assignmentModal, assignmentForm]);

  const handleExportCSV = useCallback(() => {
    const hoursFromAllocation = (a: ResourceAllocation) => a.hoursPerMonth ?? (a.percent * FULL_TIME_HOURS_PER_MONTH / 100);
    const totalByResource: Record<string, number> = {};
    allocations.forEach(a => {
      totalByResource[a.resourceId] = (totalByResource[a.resourceId] ?? 0) + hoursFromAllocation(a);
    });
    const headers = ['Resource', projectLabel, 'Hours/Month', '% FTE (160h=100%)', 'Start Date', 'End Date', 'Over-allocated'];
    const rows = allocations.map(a => {
      const resource = resources.find(r => r.id === a.resourceId);
      const product = products.find(p => p.id === a.productId);
      const hours = hoursFromAllocation(a);
      const totalHours = totalByResource[a.resourceId] ?? 0;
      const over = totalHours > FULL_TIME_HOURS_PER_MONTH ? 'Yes' : 'No';
      return [
        resource?.name ?? a.resourceId,
        product?.name ?? a.productId,
        hours,
        Math.round((hours / FULL_TIME_HOURS_PER_MONTH) * 100),
        a.startDate ? formatCalendarDateLabel(a.startDate) : '',
        a.endDate ? formatCalendarDateLabel(a.endDate) : '',
        over,
      ];
    });
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `resource-allocations-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- projectLabel derived from isStandalone (prop), stable
  }, [allocations, resources, products]);

  const projectLabel = isStandalone ? 'Project' : 'Product';
  const projectsLabel = isStandalone ? 'Projects' : 'Products';

  const handleCreateProject = async () => {
    if (!user || !newProjectName.trim()) return;
    try {
      setCreatingProject(true);
      setError(null);
      let columnId: string | null | undefined = projectsColumnId;
      if (!columnId) {
        columnId = await ensureResourceManagerProjectsColumn(boardId);
        if (!columnId) {
          setError('Could not create or find Projects column. Please refresh and try again.');
          return;
        }
      }
      const productId = await addProduct(
        {
          name: newProjectName.trim(),
          description: undefined,
          effortLevel: 'Medium',
          marketPotential: 'Medium',
          gtmReadyPercent: 0,
          status: 'Ideas',
          features: [],
          columnId: columnId as string,
          boardId,
          createdBy: user.id,
          startDate: parseOptionalDateInput(newProjectStartDate),
          endDate: parseOptionalDateInput(newProjectEndDate),
          pricingModel: newProjectPricingModel,
        },
        user.id
      );
      for (let i = 0; i < newProjectPlaceholderSlots.length; i++) {
        const roleName = newProjectPlaceholderSlots[i]?.trim();
        if (!roleName) continue;
        const idx = newProjectPlaceholderSlots.slice(0, i + 1).filter((r) => r === roleName).length;
        const name = `Placeholder ${roleName} ${idx}`;
        const resourceId = await addResource(
          {
            name,
            role: roleName,
            boardId,
            isPlaceholder: true,
            createdBy: user.id,
          },
          user.id
        );
        await upsertAllocation(
          {
            resourceId,
            productId,
            boardId,
            percent: 0,
            monthlyHours: {},
            createdBy: user.id,
          },
          user.id
        );
      }
      setNewProjectName('');
      setNewProjectStartDate('');
      setNewProjectEndDate('');
      setNewProjectPricingModel('time_and_materials');
      setNewProjectPlaceholderSlots([]);
    } catch (err) {
      setError('Failed to create project');
      console.error(err);
    } finally {
      setCreatingProject(false);
    }
  };

  const handleUpdateProject = async (productId: string, updates: {
    name?: string;
    description?: string;
    startDate?: Date | null;
    endDate?: Date | null;
    pricingModel?: ProductPricingModel;
  }) => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);
      const payload: Partial<Product> = { name: updates.name, description: updates.description };
      if (updates.startDate !== undefined) payload.startDate = updates.startDate || undefined;
      if (updates.endDate !== undefined) payload.endDate = updates.endDate || undefined;
      if (updates.pricingModel !== undefined) payload.pricingModel = updates.pricingModel;
      await updateProduct(productId, payload);
      setEditingProject(null);
    } catch (err) {
      setError('Failed to update project');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (product: Product) => {
    if (!user) return;
    try {
      setDeletingProject(true);
      setError(null);
      const allocationsForProduct = allocations.filter(a => a.productId === product.id);
      for (const a of allocationsForProduct) {
        await deleteAllocation(a.id);
      }
      await deleteProduct(product.id);
      setProjectToDelete(null);
    } catch (err) {
      setError('Failed to delete project');
      console.error(err);
    } finally {
      setDeletingProject(false);
    }
  };

  if (!isOpen && !isStandalone) return null;

  const content = (
    <>
    <div className={`bg-white rounded-lg shadow-xl w-full max-w-6xl overflow-hidden ${isStandalone ? 'min-h-[calc(100vh-8rem)]' : 'max-h-[90vh]'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold">
            {isStandalone && boardName ? `${boardName} — Resource Manager` : 'Resource Manager'}
          </h2>
          {overAllocatedResourceIds.length > 0 && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium">
              <AlertTriangle className="h-4 w-4" />
              {overAllocatedResourceIds.length} over-allocated
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            disabled={allocations.length === 0}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export for BizOps / NS allocations"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
          {!isStandalone && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

        {/* Tabs */}
        <div className="flex border-b flex-wrap">
          <button
            onClick={() => setActiveTab('assignments')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'assignments'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Assignments
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'team'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="h-4 w-4" />
            Team
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'timeline'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Calendar className="h-4 w-4" />
            Timeline
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'roles'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BadgeDollarSign className="h-4 w-4" />
            Roles
          </button>
          {isStandalone && (
            <button
              onClick={() => setActiveTab('projects')}
              className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'projects'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FolderOpen className="h-4 w-4" />
              Projects
            </button>
          )}
          <button
            onClick={() => setActiveTab('resources')}
            className={`px-4 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'resources'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="h-4 w-4" />
            Resources
          </button>
        </div>

        {/* Content */}
        <div className={`p-6 overflow-auto ${isStandalone ? 'min-h-[60vh]' : 'max-h-[calc(90vh-140px)]'}`}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {activeTab === 'assignments' && (
            <div className="space-y-6">
              {products.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {isStandalone
                    ? 'No projects yet. Add projects in the Projects tab to assign resources.'
                    : `No ${projectsLabel.toLowerCase()} found. Create some ${projectsLabel.toLowerCase()} first to manage resource allocations.`}
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Assign projects to each resource, then plan monthly hours in the 12-month view. Capacity per month = weekdays × {WORKING_HOURS_PER_DAY}h, minus US federal holidays (observed). Use <strong>By project</strong> to add people or <span className="text-amber-800">placeholder roles</span> (highlighted until you assign someone real).
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">View:</span>
                      <div className="inline-flex rounded-lg border border-gray-300 p-0.5 bg-gray-100">
                        <button
                          type="button"
                          onClick={() => {
                            setAssignmentsView('by_resource');
                            setAssignmentsProjectFilterOpen(false);
                          }}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${assignmentsView === 'by_resource' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                          By resource
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setAssignmentsView('by_project');
                            setAssignmentsResourceFilterOpen(false);
                          }}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${assignmentsView === 'by_project' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                          By project
                        </button>
                      </div>
                    </div>
                    {assignmentsView === 'by_resource' && assignmentsResourceFilterPoolIds.length > 0 && (
                      <div className="relative flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                          <Filter className="h-4 w-4 text-gray-500 shrink-0" aria-hidden />
                          Show resources
                        </span>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              setAssignmentsResourceFilterOpen((o) => !o);
                              setAssignmentsProjectFilterOpen(false);
                              setProjectSelectResourceId(null);
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
                            aria-expanded={assignmentsResourceFilterOpen}
                            aria-haspopup="listbox"
                          >
                            {assignmentsResourceIdsFilter === null
                              ? `All (${assignmentsResourceFilterPoolIds.length})`
                              : `${assignmentsResourceIdsFilter.length} of ${assignmentsResourceFilterPoolIds.length}`}
                            <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${assignmentsResourceFilterOpen ? 'rotate-90' : ''}`} />
                          </button>
                          {assignmentsResourceFilterOpen && (
                            <>
                              <div
                                className="absolute left-0 top-full mt-1 z-30 py-2 min-w-[280px] max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
                                role="listbox"
                                aria-multiselectable
                              >
                                <div className="px-3 pb-2 mb-1 border-b border-gray-100 flex gap-3">
                                  <button
                                    type="button"
                                    className="text-xs font-medium text-blue-700 hover:text-blue-900"
                                    onClick={() => setAssignmentsResourceIdsFilter(null)}
                                  >
                                    Select all
                                  </button>
                                  <button
                                    type="button"
                                    className="text-xs font-medium text-gray-600 hover:text-gray-900"
                                    onClick={() => setAssignmentsResourceIdsFilter([])}
                                  >
                                    Clear
                                  </button>
                                </div>
                                {resourcesMatchingRoleFilter.map((r) => (
                                  <label
                                    key={r.id}
                                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={
                                        assignmentsResourceIdsFilter === null ||
                                        assignmentsResourceIdsFilter.includes(r.id)
                                      }
                                      onChange={() => toggleAssignmentsResourceFilter(r.id)}
                                      className="rounded border-gray-300"
                                    />
                                    <span className="text-sm text-gray-900">
                                      {r.name}
                                      <span className="text-gray-500"> · {r.role}</span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                              <div
                                className="fixed inset-0 z-20"
                                aria-hidden
                                onClick={() => setAssignmentsResourceFilterOpen(false)}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {assignmentsView === 'by_project' && assignmentsProductFilterPoolIds.length > 0 && (
                      <div className="relative flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                          <Filter className="h-4 w-4 text-gray-500 shrink-0" aria-hidden />
                          Show projects
                        </span>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              setAssignmentsProjectFilterOpen((o) => !o);
                              setAssignmentsResourceFilterOpen(false);
                              setProjectSelectResourceId(null);
                              setResourceSelectProductId(null);
                              setPlaceholderRolePickerProductId(null);
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
                            aria-expanded={assignmentsProjectFilterOpen}
                            aria-haspopup="listbox"
                          >
                            {assignmentsProductIdsFilter === null
                              ? `All (${assignmentsProductFilterPoolIds.length})`
                              : `${assignmentsProductIdsFilter.length} of ${assignmentsProductFilterPoolIds.length}`}
                            <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${assignmentsProjectFilterOpen ? 'rotate-90' : ''}`} />
                          </button>
                          {assignmentsProjectFilterOpen && (
                            <>
                              <div
                                className="absolute left-0 top-full mt-1 z-30 py-2 min-w-[260px] max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
                                role="listbox"
                                aria-multiselectable
                              >
                                <div className="px-3 pb-2 mb-1 border-b border-gray-100 flex gap-3">
                                  <button
                                    type="button"
                                    className="text-xs font-medium text-blue-700 hover:text-blue-900"
                                    onClick={() => setAssignmentsProductIdsFilter(null)}
                                  >
                                    Select all
                                  </button>
                                  <button
                                    type="button"
                                    className="text-xs font-medium text-gray-600 hover:text-gray-900"
                                    onClick={() => setAssignmentsProductIdsFilter([])}
                                  >
                                    Clear
                                  </button>
                                </div>
                                {productsSortedByName.map((p) => (
                                  <label
                                    key={p.id}
                                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={
                                        assignmentsProductIdsFilter === null ||
                                        assignmentsProductIdsFilter.includes(p.id)
                                      }
                                      onChange={() => toggleAssignmentsProductFilter(p.id)}
                                      className="rounded border-gray-300"
                                    />
                                    <span className="text-sm text-gray-900">{p.name}</span>
                                  </label>
                                ))}
                              </div>
                              <div
                                className="fixed inset-0 z-20"
                                aria-hidden
                                onClick={() => setAssignmentsProjectFilterOpen(false)}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {assignmentsView === 'by_resource' && resources.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <label htmlFor="assignment-role-filter" className="text-sm font-medium text-gray-700">
                        Filter by role
                      </label>
                      <select
                        id="assignment-role-filter"
                        value={resourceViewRoleFilter}
                        onChange={(e) => setResourceViewRoleFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
                      >
                        <option value="all">All roles</option>
                        {roleNameOptions.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {assignmentsView === 'by_resource' && (
                    resources.length === 0 ? (
                      <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="font-medium mb-1">No people in the roster yet</p>
                        <p className="text-amber-800/90">
                          Add named resources in the Resources tab, or switch to <strong>By project</strong> to add placeholder roles (you can replace them with real people when ready).
                        </p>
                      </div>
                    ) : resourcesMatchingRoleFilter.length === 0 ? (
                      <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="font-medium mb-1">No resources with this role</p>
                        <p className="text-gray-600 mb-3">
                          Try another role or show everyone again.
                        </p>
                        <button
                          type="button"
                          onClick={() => setResourceViewRoleFilter('all')}
                          className="text-sm font-medium text-blue-700 hover:text-blue-900"
                        >
                          Show all roles
                        </button>
                      </div>
                    ) : resourcesVisibleInAssignmentsByResource.length === 0 ? (
                      <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="font-medium mb-1">No resources match this filter</p>
                        <p className="text-gray-600 mb-3">
                          Select one or more people in <strong>Show resources</strong>, or show everyone again.
                        </p>
                        <button
                          type="button"
                          onClick={() => setAssignmentsResourceIdsFilter(null)}
                          className="text-sm font-medium text-blue-700 hover:text-blue-900"
                        >
                          Show all resources
                        </button>
                      </div>
                    ) : (
                      resourcesVisibleInAssignmentsByResource.map(resource => {
                    const assignedIds = getAssignedProductIds(resource.id);
                    const assignedProducts = products.filter(p => assignedIds.includes(p.id));
                    const assignedProductIds = assignedProducts.map(p => p.id);
                    const avgPct3Mo = getResourceAvgAllocationPctNext3Months(resource.id, assignedProductIds);
                    const isDropdownOpen = projectSelectResourceId === resource.id;
                    return (
                      <div key={resource.id} className={`rounded-lg border bg-white overflow-visible ${resource.isPlaceholder ? 'border-amber-400 ring-2 ring-amber-200/80 shadow-sm' : 'border-gray-200'}`}>
                        <div className={`px-4 py-3 flex flex-wrap items-center gap-3 ${resource.isPlaceholder ? 'bg-amber-50/90 border-b border-amber-200' : 'bg-gray-50'} ${assignedProducts.length > 0 && !resource.isPlaceholder ? 'border-b border-gray-200' : ''} ${assignedProducts.length > 0 && resource.isPlaceholder ? 'border-b border-amber-200' : ''} ${assignedProducts.length === 0 && !resource.isPlaceholder ? 'pb-3' : ''} ${assignedProducts.length === 0 && resource.isPlaceholder ? 'pb-3' : ''}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900">{resource.name}</span>
                              {resource.isPlaceholder && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-amber-900 bg-amber-200/90 px-1.5 py-0.5 rounded">
                                  Placeholder
                                </span>
                              )}
                              {avgPct3Mo !== null && (
                                <span className="text-xs font-medium text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded">
                                  {avgPct3Mo}% avg (3 mo)
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{resource.role}</div>
                          </div>
                          <div className="relative shrink-0">
                            <button
                              type="button"
                              onClick={() => setProjectSelectResourceId(isDropdownOpen ? null : resource.id)}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                                assignedIds.length === 0
                                  ? 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
                                  : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {assignedIds.length === 0 ? 'Assign projects' : `Projects (${assignedIds.length})`}
                              <ChevronRight className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-90' : ''}`} />
                            </button>
                            {isDropdownOpen && (
                              <>
                                <div className="absolute left-0 top-full mt-1 z-20 py-1 min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg">
                                  {productsSortedByName.map(product => {
                                    const isAssigned = assignedIds.includes(product.id);
                                    return (
                                      <label
                                        key={product.id}
                                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isAssigned}
                                          onChange={(e) => {
                                            handleProjectSelect(resource.id, product.id, e.target.checked);
                                          }}
                                          className="rounded border-gray-300"
                                        />
                                        <span className="text-sm text-gray-900">{product.name}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                                <div
                                  className="fixed inset-0 z-10"
                                  aria-hidden
                                  onClick={() => setProjectSelectResourceId(null)}
                                />
                              </>
                            )}
                          </div>
                        </div>
                        {assignedProducts.length > 0 && (
                          <div className="p-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">12-month allocation (hours)</h4>
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse text-sm">
                                <thead>
                                  <tr className="border-b border-gray-200">
                                    <th className="text-left p-2 font-medium text-gray-600 w-28">Project</th>
                                    {getMonthKeys().map(monthKey => {
                                      const [y, m] = monthKey.split('-');
                                      const monthName = new Date(parseInt(y, 10), parseInt(m, 10) - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                                      const monthTotal = getResourceMonthTotal(resource.id, monthKey, assignedProductIds);
                                      const cap = getMonthCapacityHours(monthKey);
                                      const isOver = monthTotal > cap;
                                      return (
                                        <th
                                          key={monthKey}
                                          style={getMonthColumnStyle(monthTotal, monthKey)}
                                          className={`p-2 text-center font-medium min-w-[58px] ${isOver ? 'text-red-800 font-bold ring-1 ring-red-400 ring-inset' : 'text-gray-700'}`}
                                        >
                                          <div className="flex flex-col items-center leading-tight gap-0.5">
                                            <span>{monthName}</span>
                                            <span className={`text-[10px] font-normal tabular-nums ${isOver ? 'text-red-900/85' : 'text-gray-600'}`}>
                                              {cap}h max
                                            </span>
                                          </div>
                                        </th>
                                      );
                                    })}
                                  </tr>
                                </thead>
                                <tbody>
                                  {assignedProducts.map(product => (
                                    <tr key={product.id} className="border-b border-gray-100">
                                      <td className="p-2 text-gray-900 font-medium bg-gray-50/50">{product.name}</td>
                                      {getMonthKeys().map(monthKey => {
                                        const monthTotal = getResourceMonthTotal(resource.id, monthKey, assignedProductIds);
                                        return (
                                        <td key={monthKey} className="p-1" style={getMonthColumnStyle(monthTotal, monthKey)}>
                                          <input
                                            type="number"
                                            min={0}
                                            step={10}
                                            value={getMonthlyDisplayValue(resource.id, product.id, monthKey)}
                                            onFocus={() => setMonthlyEdit({ resourceId: resource.id, productId: product.id, monthKey, value: getMonthlyDisplayValue(resource.id, product.id, monthKey) })}
                                            onChange={(e) => setMonthlyEdit(prev =>
                                              prev?.resourceId === resource.id && prev?.productId === product.id && prev?.monthKey === monthKey
                                                ? { ...prev, value: e.target.value }
                                                : prev
                                            )}
                                            onBlur={() => {
                                              const val = monthlyEdit?.resourceId === resource.id && monthlyEdit?.productId === product.id && monthlyEdit?.monthKey === monthKey
                                                ? monthlyEdit.value
                                                : getMonthlyDisplayValue(resource.id, product.id, monthKey);
                                              const hours = parseInt(val, 10) || 0;
                                              handleSaveMonthlyHours(resource.id, product.id, monthKey, hours);
                                            }}
                                            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                                            className="w-12 px-1 py-0.5 border rounded text-center text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/70"
                                            placeholder="0"
                                          />
                                        </td>
                                      ); })}
                                    </tr>
                                  ))}
                                  <tr className="border-t-2 border-gray-200 font-medium">
                                    <td className="p-2 text-gray-700 bg-gray-50">Total</td>
                                    {getMonthKeys().map(monthKey => {
                                      const total = getResourceMonthTotal(resource.id, monthKey, assignedProductIds);
                                      const cap = getMonthCapacityHours(monthKey);
                                      const isOver = total > cap;
                                      return (
                                        <td
                                          key={monthKey}
                                          style={getMonthColumnStyle(total, monthKey)}
                                          className={`p-2 text-center ${isOver ? 'text-red-800 font-bold' : 'text-gray-900'}`}
                                        >
                                          {total}h
                                        </td>
                                      );
                                    })}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                      })
                    )
                  )}
                  {assignmentsView === 'by_project' && productsVisibleInAssignmentsByProject.length === 0 ? (
                    <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="font-medium mb-1">No projects match this filter</p>
                      <p className="text-gray-600 mb-3">
                        Select one or more projects in <strong>Show projects</strong>, or show all again.
                      </p>
                      <button
                        type="button"
                        onClick={() => setAssignmentsProductIdsFilter(null)}
                        className="text-sm font-medium text-blue-700 hover:text-blue-900"
                      >
                        Show all projects
                      </button>
                    </div>
                  ) : assignmentsView === 'by_project' &&
                    productsVisibleInAssignmentsByProject.map((product) => {
                    const assignedResources = getResourcesForProject(product.id);
                    const addResOpen = resourceSelectProductId === product.id;
                    const placeholderOpen = placeholderRolePickerProductId === product.id;
                    return (
                      <div key={product.id} className="rounded-lg border border-gray-200 bg-white overflow-visible">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="font-medium text-gray-900">{product.name}</div>
                            <div className="text-sm text-gray-500">
                              {assignedResources.length} resource{assignedResources.length !== 1 ? 's' : ''} assigned
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 shrink-0">
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => {
                                  setResourceSelectProductId(addResOpen ? null : product.id);
                                  setPlaceholderRolePickerProductId(null);
                                }}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                                  addResOpen
                                    ? 'text-blue-800 bg-blue-100 border-blue-300'
                                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <UserPlus className="h-4 w-4" />
                                Add resource
                                <ChevronRight className={`h-4 w-4 transition-transform ${addResOpen ? 'rotate-90' : ''}`} />
                              </button>
                              {addResOpen && (
                                <>
                                  <div className="absolute right-0 top-full mt-1 z-20 py-1 min-w-[220px] max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                                    {nonPlaceholderResourcesSortedByName.length === 0 ? (
                                      <div className="px-3 py-2 text-xs text-gray-500">No people in roster. Add resources in the Resources tab.</div>
                                    ) : (
                                      nonPlaceholderResourcesSortedByName.map((r) => {
                                        const assignedIds = getAssignedProductIds(r.id);
                                        const isAssigned = assignedIds.includes(product.id);
                                        return (
                                          <label
                                            key={r.id}
                                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isAssigned}
                                              onChange={(e) => {
                                                handleProjectSelect(r.id, product.id, e.target.checked);
                                              }}
                                              className="rounded border-gray-300"
                                            />
                                            <span className="text-sm text-gray-900">
                                              {r.name}
                                              <span className="text-gray-500"> · {r.role}</span>
                                            </span>
                                          </label>
                                        );
                                      })
                                    )}
                                  </div>
                                  <div
                                    className="fixed inset-0 z-10"
                                    aria-hidden
                                    onClick={() => setResourceSelectProductId(null)}
                                  />
                                </>
                              )}
                            </div>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => {
                                  setPlaceholderRolePickerProductId(placeholderOpen ? null : product.id);
                                  setResourceSelectProductId(null);
                                }}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                                  placeholderOpen
                                    ? 'text-amber-900 bg-amber-100 border-amber-300'
                                    : 'text-amber-900 bg-amber-50 border-amber-200 hover:bg-amber-100'
                                }`}
                              >
                                <UserCircle className="h-4 w-4" />
                                Add placeholder
                                <ChevronRight className={`h-4 w-4 transition-transform ${placeholderOpen ? 'rotate-90' : ''}`} />
                              </button>
                              {placeholderOpen && (
                                <>
                                  <div className="absolute right-0 top-full mt-1 z-20 py-1 min-w-[200px] bg-white border border-amber-200 rounded-lg shadow-lg">
                                    {roleNameOptions.length === 0 ? (
                                      <div className="px-3 py-2 text-xs text-gray-500">No roles defined.</div>
                                    ) : (
                                      roleNameOptions.map((roleName) => (
                                        <button
                                          key={roleName}
                                          type="button"
                                          className="w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-amber-50"
                                          onClick={() => void handleAddPlaceholderToProject(product.id, roleName)}
                                        >
                                          {roleName}
                                        </button>
                                      ))
                                    )}
                                  </div>
                                  <div
                                    className="fixed inset-0 z-10"
                                    aria-hidden
                                    onClick={() => setPlaceholderRolePickerProductId(null)}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        {assignedResources.length > 0 ? (
                          <div className="p-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">12-month allocation (hours)</h4>
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse text-sm">
                                <thead>
                                  <tr className="border-b border-gray-200">
                                    <th className="text-left p-2 font-medium text-gray-600 w-28">Resource</th>
                                    {getMonthKeys().map(monthKey => {
                                      const [y, m] = monthKey.split('-');
                                      const monthName = new Date(parseInt(y, 10), parseInt(m, 10) - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                                      const cap = getMonthCapacityHours(monthKey);
                                      return (
                                        <th key={monthKey} className="p-2 text-center font-medium text-gray-700 min-w-[58px]">
                                          <div className="flex flex-col items-center leading-tight gap-0.5">
                                            <span>{monthName}</span>
                                            <span className="text-[10px] font-normal text-gray-500 tabular-nums">{cap}h max</span>
                                          </div>
                                        </th>
                                      );
                                    })}
                                  </tr>
                                </thead>
                                <tbody>
                                  {assignedResources.map(resource => {
                                    const alloc = allocations.find((a) => a.resourceId === resource.id && a.productId === product.id);
                                    return (
                                    <tr
                                      key={resource.id}
                                      className={`border-b border-gray-100 ${resource.isPlaceholder ? 'bg-amber-50/70 ring-1 ring-inset ring-amber-300/90' : ''}`}
                                    >
                                      <td
                                        className={`p-2 font-medium align-top ${resource.isPlaceholder ? 'bg-amber-50/90 cursor-pointer hover:bg-amber-100/90' : 'bg-gray-50/50'}`}
                                        onClick={() => {
                                          if (!resource.isPlaceholder || !alloc) return;
                                          setReplaceModal({
                                            allocationId: alloc.id,
                                            placeholderId: resource.id,
                                            placeholderLabel: resource.name,
                                            productId: product.id,
                                            productName: product.name,
                                          });
                                          setReplacementResourceId('');
                                        }}
                                        title={resource.isPlaceholder ? 'Click to assign a real person to this slot' : undefined}
                                      >
                                        <div className="flex items-start gap-2 flex-wrap">
                                          <div>
                                            <div className="text-gray-900">{resource.name}</div>
                                            <div className="text-xs text-gray-500">{resource.role}</div>
                                          </div>
                                          {resource.isPlaceholder && (
                                            <span className="text-[10px] font-bold uppercase tracking-wide text-amber-900 bg-amber-200/90 px-1.5 py-0.5 rounded shrink-0">
                                              Placeholder
                                            </span>
                                          )}
                                        </div>
                                        {resource.isPlaceholder && (
                                          <div className="text-[11px] text-amber-800 mt-1">Click to replace with someone on the roster</div>
                                        )}
                                      </td>
                                      {getMonthKeys().map(monthKey => {
                                        const resourceMonthTotal = getResourceMonthTotal(resource.id, monthKey, getAssignedProductIds(resource.id));
                                        return (
                                          <td key={monthKey} className="p-1" style={getMonthColumnStyle(resourceMonthTotal, monthKey)}>
                                            <input
                                              type="number"
                                              min={0}
                                              step={10}
                                              value={getMonthlyDisplayValue(resource.id, product.id, monthKey)}
                                              onFocus={() => setMonthlyEdit({ resourceId: resource.id, productId: product.id, monthKey, value: getMonthlyDisplayValue(resource.id, product.id, monthKey) })}
                                              onChange={(e) => setMonthlyEdit(prev =>
                                                prev?.resourceId === resource.id && prev?.productId === product.id && prev?.monthKey === monthKey
                                                  ? { ...prev, value: e.target.value }
                                                  : prev
                                              )}
                                              onBlur={() => {
                                                const val = monthlyEdit?.resourceId === resource.id && monthlyEdit?.productId === product.id && monthlyEdit?.monthKey === monthKey
                                                  ? monthlyEdit.value
                                                  : getMonthlyDisplayValue(resource.id, product.id, monthKey);
                                                const hours = parseInt(val, 10) || 0;
                                                handleSaveMonthlyHours(resource.id, product.id, monthKey, hours);
                                              }}
                                              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                                              onClick={(e) => resource.isPlaceholder && e.stopPropagation()}
                                              className="w-12 px-1 py-0.5 border rounded text-center text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/70"
                                              placeholder="0"
                                            />
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                  })}
                                  <tr className="border-t-2 border-gray-200 font-medium">
                                    <td className="p-2 text-gray-700 bg-gray-50">Total</td>
                                    {getMonthKeys().map(monthKey => {
                                      const total = getProjectMonthTotal(product.id, monthKey);
                                      return (
                                        <td key={monthKey} className="p-2 text-center text-gray-900">
                                          {total}h
                                        </td>
                                      );
                                    })}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 text-sm text-gray-600 space-y-2">
                            <p>No one assigned to this {projectLabel.toLowerCase()} yet.</p>
                            <p className="text-gray-500">
                              Use <strong>Add resource</strong> to attach people from the roster, or <strong>Add placeholder</strong> to plan role slots you can fill later.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {activeTab === 'projects' && isStandalone && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                Add, edit, or delete projects. These projects appear in Assignments for resource allocation.
              </p>
              {/* Add project */}
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <h3 className="font-medium text-gray-900 mb-2">Add project</h3>
                <div className="flex flex-col sm:flex-row gap-2 flex-wrap items-end">
                  <input
                    type="text"
                    placeholder="Project name"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
                    className="flex-1 min-w-[200px] px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="date"
                    placeholder="Start date"
                    value={newProjectStartDate}
                    onChange={(e) => setNewProjectStartDate(e.target.value)}
                    className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                    title="Start date"
                  />
                  <input
                    type="date"
                    placeholder="End date"
                    value={newProjectEndDate}
                    onChange={(e) => setNewProjectEndDate(e.target.value)}
                    className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                    title="End date"
                  />
                  <div className="w-full sm:w-auto min-w-[200px]">
                    <label className="block text-xs font-medium text-gray-500 mb-0.5">Pricing</label>
                    <select
                      value={newProjectPricingModel}
                      onChange={(e) => setNewProjectPricingModel(e.target.value as ProductPricingModel)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                    >
                      {PRODUCT_PRICING_MODEL_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleCreateProject}
                    disabled={creatingProject || !newProjectName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {creatingProject ? 'Adding…' : 'Add project'}
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-800 mb-1">Placeholder roles (optional)</label>
                  <p className="text-xs text-gray-600 mb-2">
                    Creates highlighted slots in Assignments (By project) until you assign real people—e.g. two developers, a PM, and a designer.
                  </p>
                  <div className="flex flex-wrap gap-2 items-center mb-2">
                    <button
                      type="button"
                      onClick={() =>
                        setNewProjectPlaceholderSlots(['Developer', 'Developer', 'Project Manager', 'Designer'])
                      }
                      className="text-xs font-medium text-blue-700 hover:text-blue-900 underline"
                    >
                      Example: 2 Developers, PM, Designer
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 items-end">
                    {newProjectPlaceholderSlots.map((slot, idx) => (
                      <div key={`slot-${idx}`} className="flex items-center gap-1">
                        <select
                          value={slot}
                          onChange={(e) => {
                            const next = [...newProjectPlaceholderSlots];
                            next[idx] = e.target.value;
                            setNewProjectPlaceholderSlots(next);
                          }}
                          className="px-2 py-1.5 border rounded-lg text-sm bg-white min-w-[168px]"
                        >
                          {(roleNameOptions.length > 0 ? roleNameOptions : ['Developer']).map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() =>
                            setNewProjectPlaceholderSlots(newProjectPlaceholderSlots.filter((_, i) => i !== idx))
                          }
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Remove slot"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        setNewProjectPlaceholderSlots([
                          ...newProjectPlaceholderSlots,
                          roleNameOptions[0] || 'Developer',
                        ])
                      }
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100"
                    >
                      <Plus className="h-4 w-4" />
                      Add role slot
                    </button>
                  </div>
                </div>
              </div>
              {/* Projects list */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Projects ({products.length})</h3>
                {products.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 rounded-lg border border-dashed border-gray-300">
                    No projects yet. Add one above.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {productsSortedByName.map(product => (
                      <div
                        key={product.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        {editingProject?.id === product.id ? (
                          <div className="flex flex-col gap-2 flex-1 mr-3">
                            <input
                              type="text"
                              value={editingProject.name}
                              onChange={(e) => setEditingProject(prev => prev ? { ...prev, name: e.target.value } : null)}
                              className="px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Project name"
                            />
                            <textarea
                              value={editingProject.description ?? ''}
                              onChange={(e) => setEditingProject(prev => prev ? { ...prev, description: e.target.value || undefined } : null)}
                              className="px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                              placeholder="Description (optional)"
                              rows={2}
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-0.5">Start date</label>
                                <input
                                  type="date"
                                  value={formatDateForInput(editingProject.startDate)}
                                  onChange={(e) => setEditingProject(prev => prev ? { ...prev, startDate: parseOptionalDateInput(e.target.value) } : null)}
                                  className="w-full px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-0.5">End date</label>
                                <input
                                  type="date"
                                  value={formatDateForInput(editingProject.endDate)}
                                  onChange={(e) => setEditingProject(prev => prev ? { ...prev, endDate: parseOptionalDateInput(e.target.value) } : null)}
                                  className="w-full px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-0.5">Pricing</label>
                              <select
                                value={editingProject.pricingModel ?? 'time_and_materials'}
                                onChange={(e) => setEditingProject(prev => prev ? { ...prev, pricingModel: e.target.value as ProductPricingModel } : null)}
                                className="w-full max-w-xs px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                              >
                                {PRODUCT_PRICING_MODEL_OPTIONS.map(({ value, label }) => (
                                  <option key={value} value={value}>{label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleUpdateProject(product.id, {
                                  name: editingProject.name,
                                  description: editingProject.description ?? '',
                                  startDate: editingProject.startDate ?? null,
                                  endDate: editingProject.endDate ?? null,
                                  pricingModel: editingProject.pricingModel ?? 'time_and_materials',
                                })}
                                disabled={loading}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingProject(null)}
                                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-gray-900">{product.name}</span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                  {getProductPricingModelLabel(product.pricingModel)}
                                </span>
                              </div>
                              {product.description && (
                                <div className="text-sm text-gray-500 mt-0.5 line-clamp-2">{product.description}</div>
                              )}
                              {(product.startDate || product.endDate) && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {product.startDate && <span>Start: {formatCalendarDateLabel(product.startDate)}</span>}
                                  {product.startDate && product.endDate && ' · '}
                                  {product.endDate && <span>End: {formatCalendarDateLabel(product.endDate)}</span>}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={() => setEditingProject(product)}
                                className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                                title="Edit project"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setProjectToDelete(product)}
                                disabled={deletingProject}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Delete project"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Delete confirmation modal */}
              {projectToDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
                  <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                    <h3 className="font-semibold text-lg text-gray-900">Delete project?</h3>
                    <p className="text-sm text-gray-600 mt-2">
                      “{projectToDelete.name}” will be removed. All resource allocations for this project will also be removed. This cannot be undone.
                    </p>
                    <div className="flex justify-end gap-2 mt-6">
                      <button
                        onClick={() => setProjectToDelete(null)}
                        disabled={deletingProject}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDeleteProject(projectToDelete)}
                        disabled={deletingProject}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {deletingProject ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'team' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                Master team list with availability. Green = capacity available, yellow = near full, red = over-allocated.
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {resources.map(resource => {
                  const assignedIds = getAssignedProductIds(resource.id);
                  const avgHours3Mo = getResourceAvgHoursNext3Months(resource.id, assignedIds);
                  const avgPct3Mo = getResourceAvgAllocationPctNext3Months(resource.id, assignedIds);
                  const monthKeys3 = getMonthKeys().slice(0, 3);
                  const avgCap3Mo =
                    monthKeys3.reduce((s, k) => s + getMonthCapacityHours(k), 0) / Math.max(monthKeys3.length, 1);
                  // Use 3-month average for display and gauge when available; otherwise fall back to legacy total
                  const displayHours = avgHours3Mo ?? getResourceTotalAllocation(resource.id);
                  const roundedHours = Math.round(displayHours);
                  const isOver =
                    avgHours3Mo != null
                      ? monthKeys3.some(mk => getResourceMonthTotal(resource.id, mk, assignedIds) > getMonthCapacityHours(mk))
                      : getResourceTotalAllocation(resource.id) > FULL_TIME_HOURS_PER_MONTH;
                  const gaugeWidthPct =
                    avgPct3Mo != null ? Math.min(avgPct3Mo, 100) : Math.min((displayHours / FULL_TIME_HOURS_PER_MONTH) * 100, 100);
                  const freeNext = getFreeNextMonth(resource.id);
                  return (
                    <div
                      key={resource.id}
                      className={`rounded-xl border p-4 ${isOver ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-medium text-gray-900">{resource.name}</div>
                          <div className="text-xs text-gray-500">{resource.role}</div>
                        </div>
                        <div className="text-right">
                          <span className={`text-sm font-semibold ${isOver ? 'text-red-600' : 'text-gray-700'}`}>
                            {roundedHours} h
                          </span>
                          {avgPct3Mo !== null && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {avgPct3Mo}% avg (3 mo)
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Gauge vs 3‑mo average capacity when monthly data exists */}
                      <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${getAvailabilityColor(displayHours, avgHours3Mo != null ? avgCap3Mo : undefined)}`}
                          style={{ width: `${gaugeWidthPct}%` }}
                        />
                      </div>
                      {isOver && (
                        <div className="mt-1.5 flex items-center gap-1 text-xs text-red-600 font-medium">
                          <AlertTriangle className="h-3 w-3" />
                          Over-allocated
                        </div>
                      )}
                      {freeNext && (
                        <div className="mt-1.5 text-xs text-gray-500">
                          Free next: {freeNext}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {resources.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No resources. Add them in the Resources tab.
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm text-gray-600">
                  View allocations over time. Use arrows to see when people free up.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setTimelineMonth(prev => {
                      const d = new Date(prev);
                      d.setMonth(d.getMonth() - 1);
                      return d;
                    })}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="font-medium text-gray-800 min-w-[140px] text-center">
                    {timelineMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                  <button
                    type="button"
                    onClick={() => setTimelineMonth(prev => {
                      const d = new Date(prev);
                      d.setMonth(d.getMonth() + 1);
                      return d;
                    })}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium text-gray-700 sticky left-0 bg-white">Resource</th>
                      <th className="text-left p-2 font-medium text-gray-700 w-48">Allocations in period</th>
                      <th className="text-left p-2 font-medium text-gray-700">Frees up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resources.map(resource => {
                      const resourceAllocs = allocations.filter(a => a.resourceId === resource.id);
                      const monthStart = new Date(timelineMonth.getFullYear(), timelineMonth.getMonth(), 1);
                      const monthEnd = new Date(timelineMonth.getFullYear(), timelineMonth.getMonth() + 1, 0);
                      const inPeriod = resourceAllocs.filter(a => {
                        const start = a.startDate ? new Date(a.startDate) : null;
                        const end = a.endDate ? new Date(a.endDate) : null;
                        if (!start && !end) return true;
                        const overlaps = (!end || end >= monthStart) && (!start || start <= monthEnd);
                        return overlaps;
                      });
                      const endDates = resourceAllocs
                        .map(a => a.endDate ? new Date(a.endDate) : null)
                        .filter((d): d is Date => d !== null && d >= new Date());
                      const nextFree = endDates.length > 0 ? new Date(Math.min(...endDates.map(d => d.getTime()))) : null;
                      return (
                        <tr key={resource.id} className="border-b hover:bg-gray-50">
                          <td className="p-2 sticky left-0 bg-white">
                            <div className="font-medium">{resource.name}</div>
                            <div className="text-xs text-gray-500">{resource.role}</div>
                          </td>
                          <td className="p-2">
                            {inPeriod.length === 0 ? (
                              <span className="text-gray-400">—</span>
                            ) : (
                              <ul className="space-y-0.5">
                                {inPeriod.map(a => {
                                  const product = products.find(p => p.id === a.productId);
                                  const start = a.startDate ? formatCalendarDateLabel(a.startDate) : '—';
                                  const end = a.endDate ? formatCalendarDateLabel(a.endDate) : '—';
                                  return (
                                    <li key={a.id} className="text-gray-700">
                                      {product?.name ?? a.productId}: {(a.hoursPerMonth ?? (a.percent * FULL_TIME_HOURS_PER_MONTH / 100))} h/mo ({start} – {end})
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </td>
                          <td className="p-2 text-gray-600">
                            {nextFree ? nextFree.toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {resources.length === 0 && (
                <div className="text-center py-8 text-gray-500">No resources. Add them in the Resources tab.</div>
              )}
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                Define job roles with billable and internal cost rates (USD per hour). Budget rollups from assignments multiply assigned hours by these rates per resource role.
              </p>
              {resourceRoles.length === 0 && (
                <div className="flex flex-wrap items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-900 flex-1 min-w-[200px]">
                    No roles in this board yet. You can load a standard set or add roles manually.
                  </p>
                  <button
                    type="button"
                    onClick={handleSeedDefaultRoles}
                    disabled={seedingRoles || loading}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {seedingRoles ? 'Adding…' : 'Add standard roles'}
                  </button>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Add role</h3>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="min-w-[140px]">
                    <label className="block text-xs text-gray-600 mb-1">Role name</label>
                    <input
                      type="text"
                      value={newRoleForm.name}
                      onChange={(e) => setNewRoleForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Developer"
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="w-28">
                    <label className="block text-xs text-gray-600 mb-1">Bill $/hr</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={newRoleForm.billRatePerHour}
                      onChange={(e) => setNewRoleForm(f => ({ ...f, billRatePerHour: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="w-28">
                    <label className="block text-xs text-gray-600 mb-1">Cost $/hr</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={newRoleForm.costRatePerHour}
                      onChange={(e) => setNewRoleForm(f => ({ ...f, costRatePerHour: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddRole}
                    disabled={loading || !newRoleForm.name.trim()}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
              </div>
              {resourceRoles.length > 0 && (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-left">
                        <th className="p-3 font-medium text-gray-700">Role</th>
                        <th className="p-3 font-medium text-gray-700">Bill ($/hr)</th>
                        <th className="p-3 font-medium text-gray-700">Cost ($/hr)</th>
                        <th className="p-3 font-medium text-gray-700 w-40">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...resourceRoles].sort((a, b) => a.name.localeCompare(b.name)).map(role => {
                        const isEditing = editingRoleDraft?.id === role.id;
                        const row = isEditing && editingRoleDraft ? editingRoleDraft : role;
                        return (
                          <tr key={role.id} className="border-b border-gray-100 hover:bg-gray-50/80">
                            <td className="p-3 align-middle">
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={row.name}
                                  onChange={(e) => setEditingRoleDraft(prev => prev ? { ...prev, name: e.target.value } : null)}
                                  className="w-full max-w-[200px] px-2 py-1 border rounded text-sm"
                                />
                              ) : (
                                <span className="font-medium text-gray-900">{role.name}</span>
                              )}
                            </td>
                            <td className="p-3 align-middle">
                              {isEditing ? (
                                <input
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={row.billRatePerHour}
                                  onChange={(e) => setEditingRoleDraft(prev => prev ? { ...prev, billRatePerHour: parseFloat(e.target.value) || 0 } : null)}
                                  className="w-24 px-2 py-1 border rounded text-sm"
                                />
                              ) : (
                                <span className="tabular-nums">${role.billRatePerHour.toFixed(0)}</span>
                              )}
                            </td>
                            <td className="p-3 align-middle">
                              {isEditing ? (
                                <input
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={row.costRatePerHour}
                                  onChange={(e) => setEditingRoleDraft(prev => prev ? { ...prev, costRatePerHour: parseFloat(e.target.value) || 0 } : null)}
                                  className="w-24 px-2 py-1 border rounded text-sm"
                                />
                              ) : (
                                <span className="tabular-nums">${role.costRatePerHour.toFixed(0)}</span>
                              )}
                            </td>
                            <td className="p-3 align-middle">
                              {isEditing ? (
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleSaveRoleRow(role)}
                                    disabled={loading}
                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingRoleDraft(null)}
                                    className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setEditingRoleDraft({ ...role })}
                                    className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteRole(role)}
                                    disabled={loading}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                    title="Delete role"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'resources' && (
            <div className="space-y-6">
              {/* Add Resource Form */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Add New Resource</h3>
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="Resource name"
                    value={newResource.name}
                    onChange={(e) => setNewResource(prev => ({ ...prev, name: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={newResource.role}
                    onChange={(e) => setNewResource(prev => ({ ...prev, role: e.target.value }))}
                    className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {roleNameOptions.map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleAddResource}
                    disabled={loading || !newResource.name.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
              </div>

              {/* Resources List */}
              <div>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h3 className="font-medium text-gray-900">
                    Resources (
                    {resourceViewRoleFilter === 'all'
                      ? resources.length
                      : `${resourcesMatchingRoleFilter.length} of ${resources.length}`}
                    )
                  </h3>
                  {resources.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <label htmlFor="resources-tab-role-filter" className="text-sm font-medium text-gray-700">
                        Filter by role
                      </label>
                      <select
                        id="resources-tab-role-filter"
                        value={resourceViewRoleFilter}
                        onChange={(e) => setResourceViewRoleFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
                      >
                        <option value="all">All roles</option>
                        {roleNameOptions.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {resources.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No resources added yet. Add your first resource above.
                  </div>
                ) : resourcesMatchingRoleFilter.length === 0 ? (
                  <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="font-medium mb-1">No resources with this role</p>
                    <button
                      type="button"
                      onClick={() => setResourceViewRoleFilter('all')}
                      className="text-sm font-medium text-blue-700 hover:text-blue-900"
                    >
                      Show all roles
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {resourcesMatchingRoleFilter.map(resource => (
                      <div
                        key={resource.id}
                        className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 ${
                          resource.isPlaceholder ? 'border-amber-300 bg-amber-50/40 ring-1 ring-amber-200' : ''
                        }`}
                      >
                        {editingResource?.id === resource.id ? (
                          <div className="flex items-center gap-3 flex-1">
                            <input
                              type="text"
                              value={editingResource.name}
                              onChange={(e) => setEditingResource(prev => prev ? { ...prev, name: e.target.value } : null)}
                              className="px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <select
                              value={editingResource.role}
                              onChange={(e) => setEditingResource(prev => prev ? { ...prev, role: e.target.value } : null)}
                              className="px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {roleNameOptions.map(name => (
                                <option key={name} value={name}>{name}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleUpdateResource(resource.id, editingResource)}
                              disabled={loading}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingResource(null)}
                              className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{resource.name}</span>
                                {resource.isPlaceholder && (
                                  <span className="text-[10px] font-bold uppercase text-amber-900 bg-amber-200/90 px-1.5 py-0.5 rounded">
                                    Placeholder
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">{resource.role}</div>
                              <div className="mt-1.5 flex items-center gap-2">
                                <div className="w-24 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${getAvailabilityColor(getResourceTotalAllocation(resource.id))}`}
                                    style={{ width: `${Math.min((getResourceTotalAllocation(resource.id) / FULL_TIME_HOURS_PER_MONTH) * 100, 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500">{Math.round(getResourceTotalAllocation(resource.id))} h</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setEditingResource(resource)}
                                className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteResource(resource.id)}
                                disabled={loading}
                                className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Assignment modal (LOE + timeline) */}
        {replaceModal && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-[11] p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 border border-amber-200">
              <h3 className="font-semibold text-lg mb-1 text-gray-900">Replace placeholder</h3>
              <p className="text-sm text-gray-600 mb-4">
                <span className="font-medium text-amber-900">{replaceModal.placeholderLabel}</span>
                {' '}on <span className="font-medium">{replaceModal.productName}</span> will be replaced by someone from your roster. Monthly hours stay on this assignment.
              </p>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Assign to</label>
                <select
                  value={replacementResourceId}
                  onChange={(e) => setReplacementResourceId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                >
                  <option value="">Select a person…</option>
                  {nonPlaceholderResourcesSortedByName
                    .filter((r) => r.id !== replaceModal.placeholderId)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.role})
                      </option>
                    ))}
                </select>
                {nonPlaceholderResourcesSortedByName.length === 0 && (
                  <p className="text-xs text-amber-800">Add people in the Resources tab first.</p>
                )}
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setReplaceModal(null);
                    setReplacementResourceId('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleConfirmReplacePlaceholder()}
                  disabled={loading || !replacementResourceId}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  Replace placeholder
                </button>
              </div>
            </div>
          </div>
        )}

        {assignmentModal && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <h3 className="font-semibold text-lg mb-1">Assign to project</h3>
              <p className="text-sm text-gray-600 mb-4">
                {assignmentModal.resourceName} → {assignmentModal.productName}
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours per month</label>
                  <p className="text-xs text-gray-500 mb-1">
                    Typical full month ≈ {WORKING_HOURS_PER_DAY}h × weekdays minus US federal holidays (often ~150–180h).
                  </p>
                  <input
                    type="number"
                    min={0}
                    max={200}
                    step={5}
                    value={assignmentForm.hoursPerMonth}
                    onChange={(e) => setAssignmentForm(f => ({ ...f, hoursPerMonth: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
                  <input
                    type="date"
                    value={assignmentForm.startDate}
                    onChange={(e) => setAssignmentForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
                  <input
                    type="date"
                    value={assignmentForm.endDate}
                    onChange={(e) => setAssignmentForm(f => ({ ...f, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => { setAssignmentModal(null); setAssignmentForm({ hoursPerMonth: 80, startDate: '', endDate: '' }); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveAssignmentModal}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Save assignment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Remove allocation confirmation */}
      {removalConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Remove project assignment?
            </h3>
            <p className="text-sm text-gray-600 mt-2">
              <strong>{removalConfirm.resourceName}</strong> has hours entered for <strong>{removalConfirm.productName}</strong>. Removing the assignment will permanently delete those hours.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setRemovalConfirm(null)}
                disabled={removingAllocation}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemoval}
                disabled={removingAllocation}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {removingAllocation ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return isStandalone ? content : (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      {content}
    </div>
  );
};

export default ResourceManagerModal;
